import type { Account, AccountRelationship, CurrentUser, Role, User } from '@/modules/accounts'
import { capabilitiesOfUser, hasRole, isBuilder } from '@/modules/accounts'

import type { ProjectRecord, ProjectUser } from './types'

/**
 * WHO MAY DO WHAT, in one place. Selectors, route pages and every command call these same functions, so hiding a
 * button is never the only protection. They are pure: give them the index below and a user.
 *
 * These checks run in the browser, so they only SIMULATE access control for this demo. Anyone can edit browser
 * storage. A real deployment must enforce the same rules on the server.
 */

/** Lookups built once from the directory and the project assignments. */
export interface AccessIndex {
  supplierOfClient: Map<string, string>
  clientsOfSupplier: Map<string, string[]>
  clientAccountIds: string[]
  supplyLogicAccountIds: string[]
  /** userId → ids of the projects they are explicitly assigned to (ProjectUsers). */
  assignedProjectIds: Map<string, Set<string>>
  clientOfProject: Map<string, string>
}

export function buildAccessIndex(
  accounts: Account[],
  relationships: AccountRelationship[],
  projectUsers: ProjectUser[],
  projects: Pick<ProjectRecord, 'id' | 'clientAccountId'>[],
): AccessIndex {
  const supplierOfClient = new Map<string, string>()
  const clientsOfSupplier = new Map<string, string[]>()
  for (const link of relationships) {
    if (link.relationshipType !== 'SUPPLIER_CLIENT') continue
    supplierOfClient.set(link.childAccountId, link.parentAccountId)
    clientsOfSupplier.set(link.parentAccountId, [...(clientsOfSupplier.get(link.parentAccountId) ?? []), link.childAccountId])
  }
  const assignedProjectIds = new Map<string, Set<string>>()
  for (const item of projectUsers) {
    const set = assignedProjectIds.get(item.userId) ?? new Set<string>()
    set.add(item.projectId)
    assignedProjectIds.set(item.userId, set)
  }
  return {
    supplierOfClient,
    clientsOfSupplier,
    clientAccountIds: accounts.filter((account) => account.accountType === 'CLIENT').map((account) => account.id),
    supplyLogicAccountIds: accounts.filter((account) => account.accountType === 'SUPPLY_LOGIC').map((account) => account.id),
    assignedProjectIds,
    clientOfProject: new Map(projects.map((project) => [project.id, project.clientAccountId])),
  }
}

export type ProjectPersona =
  | 'SL_BUILDER'
  | 'SL_REVIEWER'
  | 'SL_APPROVER'
  | 'SUPPLIER_BUILDER'
  | 'SUPPLIER_REVIEWER'
  | 'SUPPLIER_APPROVER'
  | 'CLIENT_BUILDER'
  | 'CLIENT_REVIEWER'
  | 'CLIENT_APPROVER'

type Actor = Pick<CurrentUser, 'id' | 'role' | 'roles' | 'accountId'> & { isAccountAdmin?: boolean; account: { accountType: Account['accountType'] } }

/** Account type plus what the role does: builders (designer, editor), reviewers and approvers. */
export function personaOf(user: Actor): ProjectPersona {
  const prefix = user.account.accountType === 'SUPPLY_LOGIC' ? 'SL' : user.account.accountType
  const side = isBuilder(user) ? 'BUILDER' : hasRole(user, 'APPROVER') ? 'APPROVER' : 'REVIEWER'
  return `${prefix}_${side}` as ProjectPersona
}

/** SupplyLogic and supplier people: the side that prepares designs. */
export const isStaff = (user: Actor) => user.account.accountType !== 'CLIENT'
/** A designer or editor of the client: the person who builds the designs and puts one forward. */
export const isClientBuilder = (user: Actor) => personaOf(user) === 'CLIENT_BUILDER'
/** Reviewers and approvers only ever see the projects they are assigned to. */
export const isReviewerSide = (user: Actor) => !isBuilder(user)

export const isAssigned = (ix: AccessIndex, userId: string, projectId: string) => ix.assignedProjectIds.get(userId)?.has(projectId) ?? false

export const supplierIdOfClient = (ix: AccessIndex, clientAccountId: string) => ix.supplierOfClient.get(clientAccountId) ?? null

/** Only designers start projects. Editors work on the projects that exist, and reviewers and approvers only on assigned ones. */
export const canStartProjects = (user: Actor) => hasRole(user, 'DESIGNER')

/** Clients this person may create projects for: designers only. */
export function authorizedClientIds(ix: AccessIndex, user: Actor): string[] {
  if (!canStartProjects(user)) return []
  switch (user.account.accountType) {
    case 'SUPPLY_LOGIC':
      return ix.clientAccountIds
    case 'SUPPLIER':
      return ix.clientsOfSupplier.get(user.accountId) ?? []
    case 'CLIENT':
      return [user.accountId]
  }
}

type Scope = Pick<ProjectRecord, 'id' | 'clientAccountId'> & { status?: ProjectRecord['status'] }

/** Whether the client is within the user's account: the platform, the supplier's clients, or the client itself. */
function inScope(ix: AccessIndex, user: Actor, clientAccountId: string): boolean {
  switch (user.account.accountType) {
    case 'SUPPLY_LOGIC':
      return true
    case 'SUPPLIER':
      return ix.supplierOfClient.get(clientAccountId) === user.accountId
    case 'CLIENT':
      return clientAccountId === user.accountId
  }
}

/** A project still being drafted is not ready for anyone to review. Once it has gone for review (or beyond), reviewers and approvers can see it. */
const readyForReviewers = (project: Scope) => project.status !== 'DRAFT' && project.status !== 'IN_PROGRESS'

/** A client's account admin: sees every project of the client, and is the one who puts the client's people on them. */
const isClientAdmin = (user: Actor) => user.account.accountType === 'CLIENT' && Boolean(user.isAccountAdmin)

/**
 * Whether the user may open the project at all. Used by the project list, the project route and every command.
 * SupplyLogic sees every project and a supplier sees its own clients' projects (designers and editors; their reviewers and approvers
 * only the ones they are put on). A client's people see only the projects they are put on (the project's team, kept as project
 * mappings): the client's account admin is the exception, who sees all of the client's projects in order to put people on them.
 * Reviewers and approvers see a project only once it is in review (or has been reviewed): a draft is not theirs to look at yet.
 */
export function canViewProject(ix: AccessIndex, user: Actor, project: Scope): boolean {
  if (!inScope(ix, user, project.clientAccountId)) return false
  const assigned = isAssigned(ix, user.id, project.id)
  if (user.account.accountType === 'CLIENT') {
    if (!isClientAdmin(user) && !assigned) return false
    return isBuilder(user) || readyForReviewers(project)
  }
  if (isBuilder(user)) return true
  return readyForReviewers(project) && assigned
}

export const canCreateProjectFor = (ix: AccessIndex, user: Actor, clientAccountId: string) =>
  canStartProjects(user) && authorizedClientIds(ix, user).includes(clientAccountId)

/** An approved, completed or cancelled project is closed: nothing about it can be edited any more. */
export const isProjectOpen = (project: Pick<ProjectRecord, 'status'>) => project.status !== 'CANCELLED' && project.status !== 'COMPLETED' && project.status !== 'APPROVED'

/** Edit details, upload, prepare and share a master, customize, generate (designers and editors). */
export const canManageProject = (ix: AccessIndex, user: Actor, project: ProjectRecord) =>
  isBuilder(user) && canViewProject(ix, user, project) && isProjectOpen(project)

/**
 * Sending a version for the client's review, and choosing the client's people, stay open once the design is shared, even after the project
 * was approved (sending a version reopens it for the client's review). Only a designer or editor who may generate and see the project, and
 * never a cancelled one.
 */
export const canSendDesign = (ix: AccessIndex, user: Actor, project: ProjectRecord) =>
  isBuilder(user) && capabilitiesOfUser(user).generate && canViewProject(ix, user, project) && project.status !== 'CANCELLED'

/** Generating design versions needs the role's `generate` capability (approvers and reviewers never have it). */
export const canGenerateDesigns = (ix: AccessIndex, user: Actor, project: ProjectRecord) =>
  canManageProject(ix, user, project) && capabilitiesOfUser(user).generate

/** Putting a design forward for approval is the client's decision, made by its designers and editors. */
export const canSelectCandidate = (ix: AccessIndex, user: Actor, project: ProjectRecord) =>
  isClientBuilder(user) && canManageProject(ix, user, project)

export const canChooseApprovalRoute = canSelectCandidate

/** Approving from the client workspace needs the approver role as well: a client designer or editor can edit and add versions, but cannot approve. */
export const canApproveFromWorkspace = (ix: AccessIndex, user: Actor, project: ProjectRecord) =>
  canSelectCandidate(ix, user, project) && hasRole(user, 'APPROVER')

/** Final approval is only ever a client approver's, and only on a project they are assigned to. SupplyLogic and suppliers can never approve for a client. */
export const canApproveDirect = (ix: AccessIndex, user: Actor, project: ProjectRecord) =>
  user.account.accountType === 'CLIENT' && hasRole(user, 'APPROVER') && canViewProject(ix, user, project) && isProjectOpen(project)

/** Designers and editors name the reviewers and approvers, within their account's scope; a client's account admin puts the client's own people on its projects. */
export const canAssignParticipants = (ix: AccessIndex, user: Actor, project: ProjectRecord) =>
  canManageProject(ix, user, project) || (isClientAdmin(user) && canViewProject(ix, user, project) && isProjectOpen(project))

/**
 * Who a project may name. Approvers are the client's own: only a client approver gives the final sign-off. Reviewers may
 * also come from the managing supplier or SupplyLogic, for an internal check before the client's.
 */
export function eligibleParticipants(ix: AccessIndex, users: User[], project: Pick<ProjectRecord, 'clientAccountId'>, role: Extract<Role, 'APPROVER' | 'REVIEWER'>): User[] {
  const sides = new Set([project.clientAccountId])
  if (role === 'REVIEWER') {
    const supplier = ix.supplierOfClient.get(project.clientAccountId)
    if (supplier) sides.add(supplier)
    for (const id of ix.supplyLogicAccountIds) sides.add(id)
  }
  return users.filter((user) => sides.has(user.accountId) && hasRole(user, role))
}
